import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useWatch } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	TAX_INFO_FORM_FIELDS,
	TaxInfoFormValues,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/TaxInfoDrawer/utils/types.ts';
import { TaxInfoShape } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/TaxInfoDrawer/utils/TaxInfoShape.ts';
import { validateTin } from '@dealstack/frontend/shared/dataExchange/services/validation.service.ts';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { useContext, useEffect, useState } from 'react';

import { CapManageControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageEntityFlow.tsx';
import { PersonDataContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/hooks/usePersonData.ts';
import {
	createPersonTaxInfoRequest,
	updatePersonTaxInfoRequest,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/apis/useTaxInformation.ts';
import { CreatePersonTaxInformationDto, ValidateTinRes } from '@dealstack/shared/lib/generated';

const FORM_DEFAULT_VALUES = {
	[TAX_INFO_FORM_FIELDS.country]: '',
	[TAX_INFO_FORM_FIELDS.number]: '',
	[TAX_INFO_FORM_FIELDS.authority]: '',
};

export const useTaxInfoDrawer = (onClose: () => void) => {
	const tCommonUi = useTranslationCommonUi();
	const { selectedEntityId } = useContext(CapManageControllerContext);
	const { refetchTaxInfo } = useContext(PersonDataContext);
	const [manualTinMode, setManualTinMode] = useState(false);

	const taxInfoForm = useForm<TaxInfoFormValues>({
		mode: 'all',
		resolver: zodResolver(TaxInfoShape),
		defaultValues: FORM_DEFAULT_VALUES,
	});

	const taxNumber = useWatch({
		control: taxInfoForm.control,
		name: TAX_INFO_FORM_FIELDS.number,
	});
	const country = useWatch({
		control: taxInfoForm.control,
		name: TAX_INFO_FORM_FIELDS.country,
	});

	const validateTinRequest = useQuery({
		queryKey: ['validateNumber', taxNumber, country],
		queryFn: async () => {
			if (!taxNumber || !country) {
				return;
			}

			taxInfoForm.clearErrors(TAX_INFO_FORM_FIELDS.number);

			return validateTin({ countryCode: country, tin: taxNumber });
		},
		enabled: false,
	});

	useEffect(() => {
		if (!validateTinRequest.isSuccess || !validateTinRequest.data) {
			return;
		}
		populateTaxDetailsFromResponse(validateTinRequest.data);
	}, [validateTinRequest.isSuccess, validateTinRequest.data]);

	const createTaxInfoMutation = useMutation({
		mutationFn: createPersonTaxInfoRequest,
	});
	const updateTaxInfoMutation = useMutation({
		mutationFn: updatePersonTaxInfoRequest,
	});

	const onTinLookup = () => {
		if (!taxNumber || !country) {
			return;
		}

		void validateTinRequest.refetch();
	};

	const populateTaxDetailsFromResponse = (result: ValidateTinRes) => {
		const { isValid, tinType } = result;

		if (!isValid) {
			taxInfoForm.setError(TAX_INFO_FORM_FIELDS.number, {
				message: tCommonUi('tax_info.tin.invalid'),
			});
			return;
		}

		taxInfoForm.setValue(TAX_INFO_FORM_FIELDS.authority, tinType ?? '');
	};

	const onFormSubmit = async (formValues: TaxInfoFormValues, taxId?: string) => {
		const { number, country, authority } = formValues;
		const body: CreatePersonTaxInformationDto = {
			number: number ?? null,
			authority: authority ?? null,
			countryCode: country,
		};

		if (taxId) {
			updateTaxInfoMutation.mutate(
				{
					personId: selectedEntityId,
					taxId,
					body,
				},
				{
					onSuccess: () => {
						void refetchTaxInfo();
						resetFormValues();
						onClose();
					},
				}
			);
		} else {
			createTaxInfoMutation.mutate(
				{
					personId: selectedEntityId,
					body,
				},
				{
					onSuccess: () => {
						void refetchTaxInfo();
						resetFormValues();
						onClose();
					},
				}
			);
		}
	};

	const resetFormValues = () => {
		taxInfoForm.reset(FORM_DEFAULT_VALUES);
	};

	return {
		taxInfoForm,
		onFormSubmit,
		onTinLookup,
		tinLookupData: validateTinRequest.data,
		tinLookupLoading: validateTinRequest.isFetching,
		isLoading: createTaxInfoMutation.isPending,
		taxNumber,
		country,
		manualTinMode,
		setManualTinMode,
		resetFormValues,
	};
};
